/**
 * usePersistedFilters — gerencia filtros de uma tela que sao espelhados em:
 *   1. localStorage (persistencia entre sessoes)
 *   2. URL query params (deep-link / share)
 *
 * Sprint 6 polimento (debito tecnico): listas em /acessos/* (Usuarios,
 * Cargos, Auditoria, SoD) tinham logicas duplicadas de persistencia.
 *
 * Uso:
 *   const [filtros, setFiltros, limpar] = usePersistedFilters(
 *     "pharuerp:acessos:auditoria:filtros",
 *     { acao: "", resultado: "" }
 *   );
 *
 * Regras:
 * - Query string PREVALECE sobre localStorage no mount (permite deep-link).
 * - Sempre que filtros mudam, atualiza ambos (localStorage + URL).
 * - `limpar()` reseta para os defaults E limpa localStorage + URL.
 * - Chaves com valor falsy ("" ou null/undefined) sao omitidas da URL.
 */
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { getItemTenant, setItemTenant } from "../utils/tenantStorage";

// MT-4.7: filtros persistidos são dado operacional — prefixados pelo
// tenant corrente para dois logins de tenants diferentes no mesmo
// navegador não lerem os filtros um do outro. getItemTenant migra a
// chave antiga (sem prefixo) automaticamente na primeira leitura.
function _lerStorage(chave) {
  if (typeof window === "undefined") return {};
  try {
    return JSON.parse(getItemTenant(chave) || "{}") || {};
  } catch {
    return {};
  }
}

function _gravarStorage(chave, dados) {
  if (typeof window === "undefined") return;
  setItemTenant(chave, JSON.stringify(dados));
}

function _urlParaObjeto(searchParams) {
  const obj = {};
  for (const [k, v] of searchParams.entries()) {
    obj[k] = v;
  }
  return obj;
}

export function usePersistedFilters(chaveStorage, defaults) {
  const [searchParams, setSearchParams] = useSearchParams();

  const [filtros, setFiltros] = useState(() => {
    const fromQs = _urlParaObjeto(searchParams);
    if (Object.values(fromQs).some((v) => v)) {
      return { ...defaults, ...fromQs };
    }
    return { ...defaults, ..._lerStorage(chaveStorage) };
  });

  // Sincroniza storage + URL sempre que filtros mudam.
  useEffect(() => {
    _gravarStorage(chaveStorage, filtros);
    const usp = new URLSearchParams();
    Object.entries(filtros).forEach(([k, v]) => {
      if (v) usp.set(k, String(v));
    });
    setSearchParams(usp, { replace: true });
  }, [filtros, chaveStorage, setSearchParams]);

  const aplicar = useCallback((chave, valor) => {
    setFiltros((p) => ({ ...p, [chave]: valor }));
  }, []);

  const limpar = useCallback(() => {
    setFiltros(defaults);
    setSearchParams({}, { replace: true });
  }, [defaults, setSearchParams]);

  const temAlgumAtivo = Object.values(filtros).some((v) => v);

  return { filtros, setFiltros, aplicar, limpar, temAlgumAtivo };
}

export default usePersistedFilters;
